import type { MaterialsFolder, MaterialsItem } from "@/lib/materials-api";
import type { MaterialsPageContext } from "@/lib/materials-folder-tree";

/** 范围 facet：全部 / 仅企业级 / 单个品牌 */
export type ScopeFilter = "all" | "enterprise" | `brand:${string}`;

export type FileTypeFacet = "markdown" | "pdf" | "image" | "other";

export type SizeUnit = "KB" | "MB";

export type DateRangeFilter = {
  from: string;
  to: string;
};

export type SizeRangeFilter = {
  min: string;
  max: string;
  unit: SizeUnit;
};

export type MaterialsClientFilters = {
  operatorIds: number[];
  createdRange: DateRangeFilter;
  updatedRange: DateRangeFilter;
  sizeRange: SizeRangeFilter;
  fileTypes: FileTypeFacet[];
};

export const FILE_TYPE_OPTIONS: { id: FileTypeFacet; label: string }[] = [
  { id: "markdown", label: "Markdown" },
  { id: "pdf", label: "PDF" },
  { id: "image", label: "图片" },
  { id: "other", label: "其他" },
];

export const ALL_FILE_TYPE_IDS: FileTypeFacet[] = FILE_TYPE_OPTIONS.map((o) => o.id);

/** 是否已勾选全部文件类型（等价于无类型筛选） */
export function isFileTypesSelectAll(types: FileTypeFacet[]): boolean {
  return ALL_FILE_TYPE_IDS.every((id) => types.includes(id));
}

export function isFileTypeSelected(
  types: FileTypeFacet[],
  facet: FileTypeFacet | "all",
): boolean {
  if (facet === "all") return isFileTypesSelectAll(types);
  return types.includes(facet);
}

export function isFileTypesSelectAllIndeterminate(types: FileTypeFacet[]): boolean {
  return types.length > 0 && !isFileTypesSelectAll(types);
}

/**
 * 文件类型「全部」全选：勾选 → 四类齐全；再点 → 清空（无类型筛选）。
 * 子项凑齐时保持四类列表（UI 上「全部」为勾选）。
 */
export function toggleFileTypeFilter(
  types: FileTypeFacet[],
  facet: FileTypeFacet | "all",
): FileTypeFacet[] {
  if (facet === "all") {
    return isFileTypesSelectAll(types) ? [] : [...ALL_FILE_TYPE_IDS];
  }
  if (types.includes(facet)) {
    return types.filter((t) => t !== facet);
  }
  return [...types, facet];
}

export const EMPTY_DATE_RANGE: DateRangeFilter = { from: "", to: "" };
export const EMPTY_SIZE_RANGE: SizeRangeFilter = { min: "", max: "", unit: "KB" };

export function emptyClientFilters(): MaterialsClientFilters {
  return {
    operatorIds: [],
    createdRange: { ...EMPTY_DATE_RANGE },
    updatedRange: { ...EMPTY_DATE_RANGE },
    sizeRange: { ...EMPTY_SIZE_RANGE },
    // 默认全选 ≡ 无类型筛选（标签行不展示类型 chip）
    fileTypes: [...ALL_FILE_TYPE_IDS],
  };
}

export function defaultScopes(routeBrandId: string | null): ScopeFilter[] {
  return routeBrandId ? [`brand:${routeBrandId}`] : ["all"];
}

/** 兼容旧单值 API */
export function defaultScopeFilter(routeBrandId: string | null): ScopeFilter {
  return defaultScopes(routeBrandId)[0]!;
}

export function serializeScopes(scopes: ScopeFilter[]): string {
  if (scopes.length === 0 || scopes.includes("all")) return "all";
  return scopes.join(",");
}

/** 范围子项：仅企业级 + 各品牌（「全部」的全选目标） */
export function scopeChildFacets(brands: { id: number }[]): ScopeFilter[] {
  return ["enterprise", ...brands.map((b) => `brand:${b.id}` as ScopeFilter)];
}

/** 是否等价于「无筛选 / API all」（含空数组） */
export function isScopeUnfiltered(scopes: ScopeFilter[]): boolean {
  return scopes.length === 0 || (scopes.length === 1 && scopes[0] === "all");
}

/** 是否已选中全部子项（含 scopes=["all"]） */
export function isScopeSelectAll(
  scopes: ScopeFilter[],
  brands: { id: number }[],
): boolean {
  if (scopes.includes("all")) return true;
  const children = scopeChildFacets(brands);
  return children.length > 0 && children.every((c) => scopes.includes(c));
}

/**
 * 「全部」= 全选语义：勾选 → API `all`；取消勾选 → `[]`（仍序列化为 all，子项全不勾）。
 * 从 `all` 取消某一子项 → 展开为其余显式 OR 列表；子项凑齐 → 折叠回 `all`。
 */
export function toggleScope(
  scopes: ScopeFilter[],
  facet: ScopeFilter,
  brands: { id: number }[] = [],
): ScopeFilter[] {
  const children = scopeChildFacets(brands);

  if (facet === "all") {
    // 已全选 → 取消「全部」：清空子项勾选（[] 仍走 API all / 无筛选）
    if (isScopeSelectAll(scopes, brands)) return [];
    return ["all"];
  }

  // 从 API all（或视觉全选）取消某一子项 → 其余显式 scope
  if (scopes.includes("all")) {
    return children.filter((c) => c !== facet);
  }

  if (scopes.includes(facet)) {
    const next = scopes.filter((s) => s !== facet);
    // 取消到空 → 默认无筛选
    return next.length === 0 ? ["all"] : next;
  }

  const next = [...scopes.filter((s) => s !== "all"), facet];
  // 子项全部勾上 → 折叠为 API all
  if (children.length > 0 && children.every((c) => next.includes(c))) {
    return ["all"];
  }
  return next;
}

export function isScopeSelected(
  scopes: ScopeFilter[],
  facet: ScopeFilter,
  brands: { id: number }[] = [],
): boolean {
  if (facet === "all") return isScopeSelectAll(scopes, brands);
  // API all 时子项视觉上全部勾选
  if (scopes.includes("all")) return true;
  return scopes.includes(facet);
}

/** 「全部」半选：有部分子项但未全选 */
export function isScopeSelectAllIndeterminate(
  scopes: ScopeFilter[],
  brands: { id: number }[],
): boolean {
  if (isScopeSelectAll(scopes, brands) || isScopeUnfiltered(scopes)) return false;
  const children = scopeChildFacets(brands);
  return children.some((c) => scopes.includes(c));
}

export function scopeOptionLabel(
  scope: ScopeFilter,
  brands: { id: number; name: string }[],
): { facet: string; value: string } {
  // 企业级与品牌统一归入「范围」facet（弹层与标签行同一分类）
  if (scope === "all") return { facet: "范围", value: "全部" };
  if (scope === "enterprise") return { facet: "范围", value: "仅企业级" };
  if (scope.startsWith("brand:")) {
    const id = scope.slice("brand:".length);
    const brand = brands.find((b) => String(b.id) === id);
    return { facet: "范围", value: brand?.name ?? id };
  }
  return { facet: "范围", value: scope };
}

/** 在当前工作区下新建素材/文件夹时写入的 brand_id */
export function brandIdForCreate(context: MaterialsPageContext): number | null {
  if (context.kind === "enterprise") return null;
  const n = context.brandId ? Number(context.brandId) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * 当前目录及其所有子孙 folder id。
 * `folderId === null`（全部素材根）时包含 `null` 与全部文件夹 id。
 */
export function collectFolderSubtreeIds(
  folderId: number | null,
  folders: { id: number; parent_id: number | null }[],
): Set<number | null> {
  if (folderId == null) {
    const all = new Set<number | null>([null]);
    for (const folder of folders) all.add(folder.id);
    return all;
  }

  const childrenOf = new Map<number | null, number[]>();
  for (const folder of folders) {
    const list = childrenOf.get(folder.parent_id) ?? [];
    list.push(folder.id);
    childrenOf.set(folder.parent_id, list);
  }

  const allowed = new Set<number | null>([folderId]);
  const stack: number[] = [folderId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    for (const childId of childrenOf.get(id) ?? []) {
      if (allowed.has(childId)) continue;
      allowed.add(childId);
      stack.push(childId);
    }
  }
  return allowed;
}

export function isDateRangeActive(range: DateRangeFilter): boolean {
  return Boolean(range.from.trim() || range.to.trim());
}

export function isSizeRangeActive(range: SizeRangeFilter): boolean {
  return Boolean(range.min.trim() || range.max.trim());
}

export function hasActiveClientFilters(filters: MaterialsClientFilters): boolean {
  return (
    filters.operatorIds.length > 0 ||
    isDateRangeActive(filters.createdRange) ||
    isDateRangeActive(filters.updatedRange) ||
    isSizeRangeActive(filters.sizeRange) ||
    (filters.fileTypes.length > 0 && !isFileTypesSelectAll(filters.fileTypes))
  );
}

export function hasActiveFilters(client: MaterialsClientFilters): boolean {
  return hasActiveClientFilters(client);
}

export function classifyFileType(item: MaterialsItem): FileTypeFacet {
  const name = item.name.toLowerCase();
  const ct = (item.content_type ?? "").toLowerCase();
  if (name.endsWith(".md") || ct.includes("markdown") || ct === "text/x-markdown") {
    return "markdown";
  }
  if (name.endsWith(".pdf") || ct === "application/pdf") return "pdf";
  if (
    ct.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(name)
  ) {
    return "image";
  }
  return "other";
}

function parseBound(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function sizeToBytes(value: number, unit: SizeUnit): number {
  return unit === "MB" ? value * 1024 * 1024 : value * 1024;
}

function inDateRange(iso: string | undefined, range: DateRangeFilter): boolean {
  if (!isDateRangeActive(range)) return true;
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  if (range.from.trim()) {
    const from = new Date(range.from).getTime();
    if (!Number.isNaN(from) && t < from) return false;
  }
  if (range.to.trim()) {
    const to = new Date(range.to).getTime();
    if (!Number.isNaN(to) && t > to) return false;
  }
  return true;
}

export function itemMatchesClientFilters(
  item: MaterialsItem,
  filters: MaterialsClientFilters,
): boolean {
  if (
    filters.operatorIds.length > 0 &&
    !filters.operatorIds.includes(item.uploaded_by_user_id)
  ) {
    return false;
  }
  if (!inDateRange(item.created_at, filters.createdRange)) return false;
  if (!inDateRange(item.updated_at, filters.updatedRange)) return false;
  if (isSizeRangeActive(filters.sizeRange)) {
    const min = parseBound(filters.sizeRange.min);
    const max = parseBound(filters.sizeRange.max);
    const bytes = item.byte_size;
    if (min != null && bytes < sizeToBytes(min, filters.sizeRange.unit)) return false;
    if (max != null && bytes > sizeToBytes(max, filters.sizeRange.unit)) return false;
  }
  if (
    filters.fileTypes.length > 0 &&
    !isFileTypesSelectAll(filters.fileTypes) &&
    !filters.fileTypes.includes(classifyFileType(item))
  ) {
    return false;
  }
  return true;
}

/**
 * 文件夹：仅应用时间范围；操作员/大小/类型为文件向 facet，不隐藏目录（便于继续导航）。
 */
export function folderMatchesClientFilters(
  folder: MaterialsFolder,
  filters: MaterialsClientFilters,
): boolean {
  if (!inDateRange(folder.created_at, filters.createdRange)) return false;
  if (!inDateRange(folder.updated_at, filters.updatedRange)) return false;
  return true;
}

/** 将筛选边界解析为 Date（支持 ISO / 本地日期时间字符串） */
export function parseFilterDate(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** 写入筛选状态的 ISO 字符串（UTC） */
export function toFilterDateIso(date: Date): string {
  return date.toISOString();
}

/**
 * 更新起/止边界并保证 start ≤ end：越界时将当前侧钳到对侧时刻。
 * 清空某一侧时不强制另一侧。
 */
export function constrainDateRangeBound(
  range: DateRangeFilter,
  which: "from" | "to",
  nextIso: string,
): DateRangeFilter {
  if (!nextIso.trim()) {
    return which === "from" ? { ...range, from: "" } : { ...range, to: "" };
  }
  const next = parseFilterDate(nextIso);
  if (!next) {
    return which === "from" ? { ...range, from: nextIso } : { ...range, to: nextIso };
  }
  if (which === "from") {
    const to = parseFilterDate(range.to);
    if (to && next.getTime() > to.getTime()) {
      return { ...range, from: toFilterDateIso(to) };
    }
    return { ...range, from: toFilterDateIso(next) };
  }
  const from = parseFilterDate(range.from);
  if (from && next.getTime() < from.getTime()) {
    return { ...range, to: toFilterDateIso(from) };
  }
  return { ...range, to: toFilterDateIso(next) };
}

/**
 * 更新最小/最大并保证 min ≤ max（同一 unit）：越界时将当前侧钳到对侧数值。
 * 清空某一侧时不强制另一侧；负数钳到 0。
 */
export function constrainSizeRangeBound(
  range: SizeRangeFilter,
  which: "min" | "max",
  nextValue: string,
): SizeRangeFilter {
  if (!nextValue.trim()) {
    return which === "min" ? { ...range, min: "" } : { ...range, max: "" };
  }
  const parsed = parseBound(nextValue);
  if (parsed == null) {
    return which === "min" ? { ...range, min: nextValue } : { ...range, max: nextValue };
  }
  const n = Math.max(0, parsed);
  const normalized = n === parsed ? nextValue.trim() : String(n);

  if (which === "min") {
    const max = parseBound(range.max);
    if (max != null && n > max) {
      return { ...range, min: range.max.trim() };
    }
    return { ...range, min: normalized };
  }
  const min = parseBound(range.min);
  if (min != null && n < min) {
    return { ...range, max: range.min.trim() };
  }
  return { ...range, max: normalized };
}

/** 日历日比较（忽略时分）：a 是否严格晚于 b 的日期 */
export function isCalendarDayAfter(a: Date, b: Date): boolean {
  const aDay = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bDay = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return aDay > bDay;
}

/** 日历日比较（忽略时分）：a 是否严格早于 b 的日期 */
export function isCalendarDayBefore(a: Date, b: Date): boolean {
  const aDay = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bDay = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return aDay < bDay;
}

function formatFilterDateBound(value: string): string {
  if (!value.trim()) return "不限";
  const d = parseFilterDate(value);
  if (!d) return value.trim();
  return d.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function formatDateRangeTag(range: DateRangeFilter): string {
  return `${formatFilterDateBound(range.from)} ~ ${formatFilterDateBound(range.to)}`;
}

export function formatSizeRangeTag(range: SizeRangeFilter): string {
  const min = range.min.trim() || "不限";
  const max = range.max.trim() || "不限";
  return `${min}${range.min.trim() ? range.unit : ""} ~ ${max}${range.max.trim() ? range.unit : ""}`;
}

export function fileTypeLabel(id: FileTypeFacet): string {
  return FILE_TYPE_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

/** 多值 facet 展示：顿号拼接 */
export function joinFilterTagValues(values: string[]): string {
  return values.join("、");
}

/** 从当前 scopes 一次性移除一组值；清空后回退到全部 */
export function removeScopes(scopes: ScopeFilter[], toRemove: ScopeFilter[]): ScopeFilter[] {
  const drop = new Set(toRemove);
  const next = scopes.filter((s) => !drop.has(s));
  return next.length === 0 ? ["all"] : next;
}

/** 筛选条 chip 的清除语义；多值 facet 可只清单个值 */
export type MaterialsFilterTagClear =
  | { kind: "operators"; operatorIds: number[] }
  | { kind: "createdRange" }
  | { kind: "updatedRange" }
  | { kind: "sizeRange" }
  | { kind: "fileTypes"; fileTypes: FileTypeFacet[] };

export type MaterialsFilterTagValueSpec = {
  key: string;
  label: string;
  clear: MaterialsFilterTagClear;
};

export type MaterialsFilterTagSpec = {
  key: string;
  facet: string;
  /** 同一 facet 容器内的可单独清除的值 */
  values: MaterialsFilterTagValueSpec[];
};

/**
 * 构建筛选标签：同一 facet 合并为一个分类容器，多值各自可 × 清除。
 * 范围类（时间/大小）为单值容器。
 */
export function buildMaterialsFilterTagSpecs(
  client: MaterialsClientFilters,
  operators: { id: number; name: string }[],
): MaterialsFilterTagSpec[] {
  const tags: MaterialsFilterTagSpec[] = [];

  // 顺序与 MaterialsFilterPopover 面板列阅读顺序一致：文件类型 → 操作员 → 上传/更新时间 → 大小
  if (client.fileTypes.length > 0 && !isFileTypesSelectAll(client.fileTypes)) {
    tags.push({
      key: "fileTypes",
      facet: "文件类型",
      values: client.fileTypes.map((ft) => ({
        key: `fileType:${ft}`,
        label: fileTypeLabel(ft),
        clear: { kind: "fileTypes", fileTypes: [ft] },
      })),
    });
  }

  if (client.operatorIds.length > 0) {
    tags.push({
      key: "operators",
      facet: "操作员",
      values: client.operatorIds.map((opId) => ({
        key: `operator:${opId}`,
        label: operators.find((o) => o.id === opId)?.name ?? `用户 ${opId}`,
        clear: { kind: "operators", operatorIds: [opId] },
      })),
    });
  }

  if (isDateRangeActive(client.createdRange)) {
    tags.push({
      key: "created",
      facet: "上传时间",
      values: [
        {
          key: "created-range",
          label: formatDateRangeTag(client.createdRange),
          clear: { kind: "createdRange" },
        },
      ],
    });
  }

  if (isDateRangeActive(client.updatedRange)) {
    tags.push({
      key: "updated",
      facet: "更新时间",
      values: [
        {
          key: "updated-range",
          label: formatDateRangeTag(client.updatedRange),
          clear: { kind: "updatedRange" },
        },
      ],
    });
  }

  if (isSizeRangeActive(client.sizeRange)) {
    tags.push({
      key: "size",
      facet: "文件大小",
      values: [
        {
          key: "size-range",
          label: formatSizeRangeTag(client.sizeRange),
          clear: { kind: "sizeRange" },
        },
      ],
    });
  }

  return tags;
}

/** 旧单值匹配（demo/测试辅助） */
export function matchesScope(brandId: string | null, filter: ScopeFilter): boolean {
  if (filter === "all") return true;
  if (filter === "enterprise") return brandId == null;
  if (filter.startsWith("brand:")) {
    return brandId === filter.slice("brand:".length);
  }
  return true;
}
